import { Box } from '@wix/design-system';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { matchRoutes, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { classNames } from '../../../util';
import { Chatbot } from '../../chatbot';
import { selectIsReset } from '../../common/selectors/chat';
import { selectFullPageSidebarExtended } from '../../common/selectors/helpCenter';
import { experiments } from '../../experiments';
import { HeaderFullPage } from '../../help-center/components/HeaderFullPage';
import { MobileLandscape } from '../../help-center/components/MobileLandscape';
import { AppRoutePatterns, appRoutes } from '../../routes';
import { renderModel } from '../../util/renderModel';
import style from '../App.scss';
import { Header } from '../components/Header';
import { usePresentedState } from '../hooks/usePresentedState';
import { useUpdateCurrentUrl } from '../hooks/useUpdateCurrentUrl';

const CONTACT_APP_CLASSNAME = 'contact-app-container';
const CONTACT_APP_MOBILE_CLASSNAME = 'contact-app-mobile-container';
const CONTACT_APP_SIDEBAR_CLASSNAME = 'contact-app-container_with-sidebar';
const CONTACT_APP_EXTENDED_SIDEBAR_CLASSNAME = 'contact-app-container_with-extended-sidebar';

const articleRoutes = [{ path: AppRoutePatterns.ArticleByUri }, { path: AppRoutePatterns.ArticleById }];
const isMobile = renderModel.isMobile();
const isCopilot = renderModel.isCopilot();

export const AppLayout: React.FC = () => {
  const sidebarExtended = useSelector(selectFullPageSidebarExtended);
  const isChatReset = useSelector(selectIsReset);
  const navigate = useNavigate();
  const location = useLocation();
  const matchedRoute = matchRoutes(articleRoutes, location);
  const isSidebarOpened = !!matchedRoute;
  const isNewChatFullPage = experiments.is('specs.chatbot.NewChatbotFullPage', 'true') && !renderModel.isCopilot();

  usePresentedState();
  useUpdateCurrentUrl();

  useEffect(() => {
    const closeSidebar = (): void => {
      if (isSidebarOpened) {
        navigate(appRoutes.chatbot());
      }
    };

    if (isChatReset) {
      closeSidebar();
    }
  }, [isChatReset]);

  const sidebarStateClassName = sidebarExtended
    ? CONTACT_APP_EXTENDED_SIDEBAR_CLASSNAME
    : CONTACT_APP_SIDEBAR_CLASSNAME;

  const mixinClassName = classNames([
    CONTACT_APP_CLASSNAME,
    isMobile && CONTACT_APP_MOBILE_CLASSNAME,
    isSidebarOpened && sidebarStateClassName,
    isNewChatFullPage && style.newChatFullPage,
  ]);

  return (
    <Box
      className={mixinClassName}
      direction="vertical"
      flexGrow={1}
      height="100%"
      minWidth={isMobile ? '100%' : '768px'}
    >
      {!isNewChatFullPage && <HeaderFullPage />}
      {isNewChatFullPage && <Header />}
      <Box
        className={classNames([
          isNewChatFullPage && style.newChatFullPageContent,
          isCopilot && style.copilot,
        ])}
        position="relative"
        flexGrow={1}
        gap={isNewChatFullPage && isSidebarOpened ? '12px' : '0'}
        overflow="hidden"
      >
        {isNewChatFullPage ? (
          <Box width="100%" backgroundColor="#FFFFFF" borderRadius="12px">
            <Chatbot />
          </Box>
        ) : (
          <Chatbot />
        )}
        <Box
          className={classNames([
            style.contactAppSidebar,
            isCopilot && style.contactAppSidebarCopilot,
            isNewChatFullPage && style.newChatFullPageSidebar,
          ])}
        >
          <Outlet />
        </Box>
      </Box>
      {isMobile && <MobileLandscape />}
    </Box>
  );
};
